// الصفحة الرئيسيّة وصفحة الدخول (2026-10-05): المحتوى والبطاقات والتأثيرات والشعار.
// تُخزَّن ضمن إعدادات الثيم (`landing`) ويُنقّيها الخادم (`app/api/admin/theme.py`:
// `_sanitize_landing` — الحدود والقوائم المسموحة هناك). النصّ الفارغ = القيمة الافتراضيّة هنا.
import {
  Antenna, Bell, Brain, ChartColumn, Eye, Facebook, FileText, Globe, MessageSquare, Mic,
  Newspaper, Radio, Satellite, Search, Shield, TrendingUp, Tv, Twitter, Users, Youtube,
  type LucideIcon,
} from "lucide-react";

export type LandingIcon =
  | "tv" | "radio" | "antenna" | "mic" | "globe" | "facebook" | "twitter" | "youtube" | "newspaper"
  | "brain" | "chart" | "trending" | "search" | "eye" | "shield" | "file" | "users" | "bell"
  | "satellite" | "message";
export type LandingTone = "accent" | "info" | "success" | "warning" | "danger" | "severe";

export interface LandingCard { icon: LandingIcon; tone: LandingTone; title: string; text: string; show: boolean }
export interface LandingEffects {
  auras: boolean; radar: boolean; floatingIcons: boolean; wave: boolean;
  cardMotion: boolean; themeToggle: boolean; showCards: boolean;
}
export interface LandingSettings {
  name: string; tagline: string; headline: string; line1: string; line2: string;
  buttonGuest: string; buttonUser: string; footer: string;
  effects: LandingEffects;
  cards: LandingCard[] | null;   // null = البطاقات الافتراضيّة
}

export const LANDING_MAX_CARDS = 6;

export const LANDING_TEXT_FIELDS: { key: keyof LandingSettings; label: string; max: number; def: string }[] = [
  { key: "name", label: "الاسم", max: 60, def: "مركز الإعلام الذكي" },
  { key: "tagline", label: "الصفة", max: 120, def: "المنصّة الوطنيّة الفلسطينيّة للذكاء الإعلاميّ" },
  { key: "headline", label: "العنوان البارز", max: 200, def: "عين الوطن التي لا تغفل وتواكب كلّ ما يُبثّ ويُنشر في الفضاء الإعلاميّ" },
  { key: "line1", label: "السطر الأوّل", max: 200, def: "صورة متكاملة عن المشهد الإعلاميّ وقضايا الرأي العامّ لصنّاع القرار" },
  { key: "line2", label: "السطر الثاني", max: 200, def: "من الشاشات والإذاعات إلى المنصّات الرقميّة وصولًا إلى تقارير إعلاميّة منظّمة" },
  { key: "buttonGuest", label: "نصّ الزرّ للزائر", max: 30, def: "تسجيل الدخول" },
  { key: "buttonUser", label: "نصّ الزرّ للمسجَّل", max: 30, def: "الدخول إلى المنصّة" },
  { key: "footer", label: "نصّ التذييل (والسنة تُضاف تلقائيًّا)", max: 80, def: "مركز الإعلام الذكي - رام الله - فلسطين" },
];

export const LANDING_EFFECTS: { key: keyof LandingEffects; label: string; desc: string }[] = [
  { key: "auras", label: "هالات لونيّة متحرّكة", desc: "خلفيّة بحركة هادئة بلون الثيم ودرجاته" },
  { key: "radar", label: "رادار حول الشعار", desc: "دوائر نابضة حول الشعار ترمز إلى الرصد" },
  { key: "floatingIcons", label: "أيقونات إعلاميّة عائمة", desc: "أيقونات المجالات تطفو في الخلفيّة" },
  { key: "wave", label: "موجة صوت", desc: "موجة متحرّكة تحت النصّ ترمز إلى البثّ والتفريغ" },
  { key: "cardMotion", label: "بطاقات ملوّنة تتحرّك", desc: "شارات ملوّنة للبطاقات وارتفاع عند التمرير" },
  { key: "themeToggle", label: "مفتاح فاتح/داكن", desc: "إظهار زرّ تبديل الوضع للزائر" },
  { key: "showCards", label: "إظهار البطاقات", desc: "إظهار قسم البطاقات أو إخفاؤه" },
];

export const LANDING_ICONS: Record<LandingIcon, { label: string; Icon: LucideIcon }> = {
  tv: { label: "تلفاز", Icon: Tv }, radio: { label: "إذاعة", Icon: Radio }, antenna: { label: "هوائيّ", Icon: Antenna },
  mic: { label: "ميكروفون", Icon: Mic }, globe: { label: "عالم", Icon: Globe }, facebook: { label: "فيسبوك", Icon: Facebook },
  twitter: { label: "إكس/تويتر", Icon: Twitter }, youtube: { label: "يوتيوب", Icon: Youtube },
  newspaper: { label: "صحيفة", Icon: Newspaper }, brain: { label: "ذكاء", Icon: Brain }, chart: { label: "رسم بيانيّ", Icon: ChartColumn },
  trending: { label: "اتّجاه", Icon: TrendingUp }, search: { label: "بحث", Icon: Search }, eye: { label: "عين", Icon: Eye },
  shield: { label: "درع", Icon: Shield }, file: { label: "تقرير", Icon: FileText }, users: { label: "جمهور", Icon: Users },
  bell: { label: "تنبيه", Icon: Bell }, satellite: { label: "قمر صناعيّ", Icon: Satellite }, message: { label: "رسالة", Icon: MessageSquare },
};

export const LANDING_TONES: Record<LandingTone, { label: string; badge: string }> = {
  accent: { label: "لون الثيم", badge: "bg-accent-700 text-accent-foreground" },
  info: { label: "معلومات", badge: "bg-info-700 text-info-foreground" },
  success: { label: "نجاح", badge: "bg-success-700 text-success-foreground" },
  warning: { label: "تنبيه", badge: "bg-warning-700 text-warning-foreground" },
  danger: { label: "خطر", badge: "bg-danger-700 text-danger-foreground" },
  severe: { label: "عالٍ", badge: "bg-severe-700 text-severe-foreground" },
};

export const DEFAULT_LANDING_CARDS: LandingCard[] = [
  { icon: "tv", tone: "accent", title: "الرصد التلفزيونيّ والإذاعيّ", text: "متابعة البثّ المباشر وتسجيله وتفريغه نصًّا على مدار الساعة", show: true },
  { icon: "globe", tone: "success", title: "المنصّات الرقميّة", text: "رصد منصّات التواصل الاجتماعيّ والمواقع الإلكترونيّة وقضايا الرأي العامّ", show: true },
  { icon: "brain", tone: "info", title: "التحليل بالذكاء الاصطناعي", text: "تحليل دلاليّ معمّق وتقارير إعلاميّة منظّمة لصنّاع القرار", show: true },
];

export const DEFAULT_LANDING: LandingSettings = {
  name: "", tagline: "", headline: "", line1: "", line2: "", buttonGuest: "", buttonUser: "", footer: "",
  effects: { auras: true, radar: true, floatingIcons: true, wave: true, cardMotion: true, themeToggle: true, showCards: true },
  cards: null,
};

/** الإعدادات المحفوظة (أو المسوّدة) مدموجةً بالافتراضيّ — كلّ حقل فارغ يأخذ قيمته الافتراضيّة. */
export function resolveLanding(raw?: Partial<LandingSettings> | null) {
  const l = { ...DEFAULT_LANDING, ...(raw || {}) };
  const text = Object.fromEntries(
    LANDING_TEXT_FIELDS.map((f) => [f.key, ((l[f.key] as string) || "").trim() || f.def]),
  ) as Record<(typeof LANDING_TEXT_FIELDS)[number]["key"], string>;
  return {
    ...text,
    effects: { ...DEFAULT_LANDING.effects, ...(l.effects || {}) },
    cards: (l.cards ?? DEFAULT_LANDING_CARDS).filter((c) => c.show),
  };
}
